"use client";

import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, Pencil, Sparkles } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import ModelSelection from "@/components/molecules/ModelSelection";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { AppContext } from "@/lib/contexts";
import { calculateModelCost } from "@/utils/goldCostUtils";
import { Modal } from "components/atoms/Modal";
import { editImage, getInpaintingModelConfig, poll, pollEditImage } from "lib/actions";
import { AppReducerActionType, EditType, IModelConfig, UserPremiumType } from "types";
interface InpaintingModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit?: (editRequest: string, isPremium: boolean, editType: string, goldCost: number, negativePrompt?: string, cfgScale?: number, numDetections?: number, urlOrFile?: string | File, base64Image?: string, creativity?: number, model?: string) => void;
  base64Image: string; // Base64 image data to display the masked area
  originalImage: string;
  imageId: string;
  handleInpaintClick: () => void;
}
export function InpaintingModal({
  isOpen,
  onCancel,
  onSubmit,
  base64Image,
  originalImage,
  imageId,
  handleInpaintClick
}: InpaintingModalProps) {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const router = useRouter();
  const [description, setDescription] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [inpaintingStrength, setInpaintingStrength] = useState(0.8);
  const [modelConfigs, setModelConfigs] = useState<IModelConfig[]>([]);
  const [selectedModel, setSelectedModel] = useState<IModelConfig | null>(null);
  const [enhancePrompt, setEnhancePrompt] = useState(false);
  const totalGoldCost = useMemo(() => {
    return calculateModelCost(selectedModel?.modelName, state.userPremiumLevel);
  }, [selectedModel?.modelName, state.userPremiumLevel]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getInpaintingModelConfig();
        setModelConfigs(data.modelConfigs);
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, []);
  const handleEditSubmit = useCallback(async (editRequest: string, negativePrompt: string, imageId: string, editType: string, base64Image: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      toast.error("You need to be a subscribed user to use this feature.");
      return;
    }
    const formData = new FormData();
    formData.append("editRequest", editRequest);
    formData.append("imageId", imageId);
    formData.append("editType", editType);
    formData.append("base64Image", base64Image);
    formData.append("premium", enhancePrompt.toString());
    formData.append("model", selectedModel?.modelName || "");
    formData.append("cfgScale", inpaintingStrength.toString());
    if (negativePrompt) {
      formData.append("negativePrompt", negativePrompt);
    }
    setLoading(true);
    const {
      requestId,
      error
    } = await editImage(formData);
    if (error) {
      toast.error("An error occurred while submitting the edit request.");
      setLoading(false);
      return;
    }
    if (requestId) {
      dispatch({
        type: AppReducerActionType.SET_GOLD,
        payload: {
          ...state,
          gold: state.gold - totalGoldCost
        }
      });
      const {
        image,
        error
      } = await poll(async () => {
        const result = await pollEditImage(requestId);
        if (result.error && typeof result.error === "object") {
          console.log("Error details:", result.error);
        }
        return result;
      }, ({
        image,
        error
      }) => {
        if (!!image) return false;
        if (typeof error === "string") return false;
        if (typeof error === "object") return false;
        return true;
      }, 5000);
      if (error) {
        if (typeof error === "object") {
          toast.error(error.message || "An unknown error occurred while generating the image and your gold has been refunded. Please try again.");
          dispatch({
            type: AppReducerActionType.SET_GOLD,
            payload: {
              ...state,
              gold: state.gold + totalGoldCost
            }
          });
        } else if (typeof error === "string") {
          toast.error("The request timed out. Please try again.");
          dispatch({
            type: AppReducerActionType.SET_GOLD,
            payload: {
              ...state,
              gold: state.gold + totalGoldCost
            }
          });
        }
      }
      if (image && image.length > 0) {
        const newImage = Array.isArray(image) ? image[0] : image;
        router.push(`/images/${newImage.id}`);
      }
    }
    setLoading(false);
  }, [dispatch, enhancePrompt, inpaintingStrength, router, selectedModel?.modelName, state, totalGoldCost]);
  const handleSubmit = async () => {
    if (onSubmit) {
      await onSubmit(description, enhancePrompt, EditType.INPAINTING, totalGoldCost, negativePrompt, inpaintingStrength, undefined, undefined, base64Image, undefined, selectedModel?.modelName);
      onCancel();
    } else {
      await handleEditSubmit(description, negativePrompt, imageId, EditType.INPAINTING, base64Image);
      onCancel();
    }
  };
  const handleEditMaskClick = () => {
    onCancel();
    handleInpaintClick();
  };
  const handleCancel = () => {
    setLoading(false);
    setDescription("");
    setNegativePrompt("");
    setInpaintingStrength(0.8);
    setEnhancePrompt(false);
    onCancel();
  };
  const handleConfigChange = () => {};
  return <Modal open={isOpen} handleBackdropClick={handleCancel} disableBackdropClick data-sentry-element="Modal" data-sentry-component="InpaintingModal" data-sentry-source-file="index.tsx">
            <div className="relative mx-4 my-4 rounded-lg p-6 shadow-xl overflow-y-auto max-h-[calc(90vh-2rem)] max-w-4xl">
                <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-center text-white">
                    Inpainting Edit
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    <div className="space-y-6">
                        <div className="relative w-full h-64 sm:h-80 md:h-96 rounded-lg overflow-hidden border border-gray-700 transition-transform duration-300 hover:scale-105">
                            {originalImage && <Image src={originalImage} alt="Original Image" layout="fill" objectFit="cover" className="rounded-lg" />}
                            <div className="absolute inset-0 bg-black bg-opacity-40 transition-opacity duration-300 hover:bg-opacity-20" />
                            {base64Image && <Image src={base64Image} alt="Masked Area" layout="fill" objectFit="cover" className="absolute top-0 left-0 opacity-50 rounded-lg" />}
                        </div>
                        <Button variant="outline" className="w-full text-gray-200 border-gray-700 hover:bg-gray-700 hover:text-white transition-colors duration-300 ease-in-out py-4 sm:py-6 text-base sm:text-lg" onClick={handleEditMaskClick} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                            <Pencil className="mr-2 h-5 w-5" data-sentry-element="Pencil" data-sentry-source-file="index.tsx" />
                            Edit Masked Area
                        </Button>
                    </div>
                    <div className="space-y-6">
                        <div className="space-y-3">
                            <ModelSelection modelConfigs={modelConfigs} onConfigChange={handleConfigChange} selectedModel={selectedModel} setSelectedModel={setSelectedModel} data-sentry-element="ModelSelection" data-sentry-source-file="index.tsx" />
                            <span className="text-[#F0B853] text-xs">
                                Cost:{" "}
                                {calculateModelCost(selectedModel?.modelName, state.userPremiumLevel)}{" "}
                                gold
                            </span>
                            <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                    <Label htmlFor="inpainting-prompt" className="text-sm font-medium text-[#E8EAF0]" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                                        Describe the change you want to see:
                                    </Label>
                                </div>
                                <Textarea id="inpainting-prompt" value={description} onChange={e => setDescription(e.target.value)} placeholder='Don&apos;t use "change the x to a y" but rather describe the changes that will exist at the end. For example, to change elf ears to human ears for a blonde female dwarf, "Female Human with Blonde Hair and Small Rounded Ears".' className="bg-[#1A1C34] text-[#E8EAF0] border-[#2C2E52] focus:border-[#4F80CC] transition-colors duration-200 min-h-[100px]" data-sentry-element="Textarea" data-sentry-source-file="index.tsx" />
                            </div>
                            {(selectedModel?.modelName === "Flux.Dev" || selectedModel?.modelName === "Flux.Pro Fill") && <>
                                    <div className="flex justify-between items-center">
                                        <Label htmlFor="inpainting-strength" className="text-sm font-medium text-[#E8EAF0]">
                                            Strength:{" "}
                                            {inpaintingStrength.toFixed(2)}
                                        </Label>
                                    </div>
                                    <Slider id="inpainting-strength" min={0} max={1} step={0.01} value={[inpaintingStrength]} onValueChange={value => setInpaintingStrength(value[0])} className="w-full" />
                                </>}
                            {selectedModel?.hasNegativePrompt && <div className="space-y-2">
                                    <Label htmlFor="inpainting-prompt" className="text-sm font-medium text-[#E8EAF0]">
                                        Negative Prompt
                                    </Label>
                                    <Textarea id="inpainting-negative-prompt" value={negativePrompt} onChange={e => setNegativePrompt(e.target.value)} placeholder="Negative Prompts allow you to specify what you don't want to see in the image. Use keywords separated by commas. Eg: 'ugly, anime, cartoon, male.' Keep it simple and to the point!" className="bg-[#1A1C34] text-[#E8EAF0] border-[#2C2E52] focus:border-[#4F80CC] transition-colors duration-200 min-h-[100px]" />
                                </div>}
                            {selectedModel?.modelName === "Ideogram 2.0" && <div style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem"
            }}>
                                    <Switch id="usePromptExpansion" checked={enhancePrompt} onCheckedChange={setEnhancePrompt} />
                                    <div className="flex items-center space-x-2">
                                        <span>Enable Prompt Expansion</span>
                                    </div>
                                </div>}
                        </div>
                        <div className="rounded-lg bg-gradient-to-r from-amber-500/10 to-yellow-500/10 p-4">
                            <div className="text-base sm:text-lg text-amber-400 flex items-center justify-center space-x-2">
                                <Sparkles className="h-5 w-5" data-sentry-element="Sparkles" data-sentry-source-file="index.tsx" />
                                <span>
                                    Cost:{" "}
                                    {calculateModelCost(selectedModel?.modelName, state.userPremiumLevel)}{" "}
                                    gold
                                </span>{" "}
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                            <Button variant="outline" onClick={handleCancel} className="text-gray-200 border-gray-700 hover:bg-gray-700 hover:text-white transition-colors duration-300 ease-in-out py-4 px-6 sm:py-6 sm:px-8 text-base sm:text-lg" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                Cancel
                            </Button>
                            <Button onClick={handleSubmit} disabled={loading} className="!bg-emerald-600 hover:!bg-emerald-700 text-white transition-all duration-300 ease-in-out transform hover:scale-105 py-4 px-6 sm:py-6 sm:px-8 text-base sm:text-lg disabled:bg-gray-700" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                {loading ? <>
                                        <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                                        Processing...
                                    </> : <>
                                        <Sparkles className="mr-2 h-6 w-6" />
                                        Generate
                                    </>}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>;
}